import { useEffect, useRef, useState } from "react";
import { capturar, type FotoEscolhida, type FotosDoPrato } from "@/lib/image";
import {
  IconCamera, IconCheck, IconClose, IconFlashOff, IconFlip, IconImage, IconPlus,
} from "@/ui/icons";

/* ============================================================
   Passo 1 de publicar: a câmera

   Os BYTES são copiados no instante da escolha, e o que segue adiante é
   um Blob em memória — nunca o `File` original.

   Isso não é otimização. O `File` que o Android devolve aponta para um
   arquivo temporário ou para um `content://`, e ele some por conta
   própria: abrir a câmera de novo pelo mesmo `<input>` recicla o
   temporário, e o provedor da galeria solta a permissão sozinho. O
   `File` continua existindo em JS, com nome e tamanho certos, e sem
   byte nenhum. Foi assim que três fotos viraram três miniaturas
   quebradas e um "não consegui ler essa imagem (3147 KB)" só na hora de
   publicar.

   Processar cedo não bastou: a leitura ainda caía um `.then()` adiante,
   depois do `input.value = ""`. Por isso `capturar()` roda na mesma
   linha da escolha — ver `lib/image.ts`.

   De quebra: o pico de memória passa a ser uma foto por vez em vez de
   três juntas, publicar fica quase instantâneo porque não sobra
   processamento, e um erro de decodificação aparece com a câmera ainda
   aberta — quando ainda dá pra tirar outra.

   O app abre na câmera, não no formulário. É o gesto do app anterior e é
   o certo para este: o ato é fotografar o prato, o resto é preenchimento.
   Na primeira versão daqui o formulário abriu antes e a câmera virou um
   campo escondido no meio dele.

   `getUserMedia` é a câmera de verdade, dentro da tela. Quando o navegador
   recusa — permissão negada, iOS em aba comum — os dois botões de baixo
   continuam funcionando, porque `<input capture>` abre a câmera do sistema
   e não depende de permissão nenhuma.
   ============================================================ */

/* Quantas fotos uma escolha da galeria pode render de uma vez. Um prato
   tem duas ou três; o teto é pra seleção acidental, não pra atrapalhar
   quem sabe o que está fazendo. */
const DE_UMA_VEZ = 12;

/** Uma foto já reduzida e pronta pra subir, com a prévia dela. */
export interface FotoPronta extends FotosDoPrato {
  /** Object URL da versão grande. Quem cria também revoga. */
  url: string;
  /** A foto como foi escolhida — é dela que um novo corte parte, não da já reduzida. */
  origem?: FotoEscolhida;
}

/** O que a tela mostra quando a última foto não deu certo. */
export interface ErroDaFoto {
  recado: string;
  /** Linha curta que torna um print de suporte útil. */
  codigo: string;
}

export function Camera(
  { onEscolher, onSair, preparando, erro, onTentarDeNovo, onSemFoto, custoSemFoto }: {
    onEscolher: (f: FotoEscolhida) => void;
    onSair: () => void;
    /** A foto anterior ainda está sendo processada. */
    preparando?: boolean;
    /** Deu errado com a última foto — dito aqui, na hora, não no publish. */
    erro?: ErroDaFoto | null;
    /** Repete o preparo da mesma foto, relendo o arquivo do zero. */
    onTentarDeNovo?: () => void;
    /** Segue para o formulário sem foto, pagando `custoSemFoto` no placar. */
    onSemFoto?: () => void;
    custoSemFoto?: string;
  },
) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const galeria = useRef<HTMLInputElement>(null);
  const sistema = useRef<HTMLInputElement>(null);

  const [frontal, setFrontal] = useState(false);
  const [semCamera, setSemCamera] = useState(false);
  const [aviso, setAviso] = useState("");

  useEffect(() => {
    let cancelado = false;

    const parar = () => {
      stream.current?.getTracks().forEach((t) => t.stop());
      stream.current = null;
    };

    (async () => {
      parar();
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: frontal ? "user" : "environment", width: { ideal: 1920 } },
          audio: false,
        });
        if (cancelado) { s.getTracks().forEach((t) => t.stop()); return; }
        stream.current = s;
        setSemCamera(false);
        if (video.current) video.current.srcObject = s;
      } catch {
        if (!cancelado) setSemCamera(true);
      }
    })();

    return () => { cancelado = true; parar(); };
  }, [frontal]);

  function disparar() {
    const v = video.current;
    if (!v || !stream.current) { sistema.current?.click(); return; }

    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d")?.drawImage(v, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onEscolher(capturar(new File([blob], "prato.jpg", { type: blob.type || "image/jpeg" })));
      },
      "image/jpeg",
      0.92,
    );
  }

  /* A LEITURA DOS BYTES COMEÇA AQUI, NA MESMA LINHA DA ESCOLHA.

     O `File` da galeria do Android é um ponteiro pra um `content://`, e
     quem estava lendo ele era o preparo — um `.then()` adiante, depois
     do `input.value = ""`, e no caso de várias fotos só depois de a
     anterior terminar de comprimir. Nessa janela o Android já tinha
     soltado o arquivo: metadados certos, zero byte. `capturar()` copia
     pra memória antes de qualquer uma dessas coisas acontecer. */
  const pegar = (input: HTMLInputElement | null) => {
    // A galeria aceita várias de uma vez; a câmera do sistema, uma só.
    const todas = Array.from(input?.files ?? []);
    const escolhidas = todas.slice(0, DE_UMA_VEZ).map(capturar);
    if (input) input.value = "";
    /* O teto existe porque copiar os bytes é adiantado: uma seleção de
       cem fotos alocaria os cem arquivos de uma vez. Ele é dito na tela
       — descartar foto em silêncio vira o próximo relato de bug. */
    setAviso(todas.length > escolhidas.length
      ? `Dá pra mandar ${DE_UMA_VEZ} fotos por vez. Usei as ${DE_UMA_VEZ} primeiras.`
      : "");
    for (const f of escolhidas) onEscolher(f);
  };

  return (
    <div className="camera">
      <div className="camera-top">
        <button className="side" onClick={onSair} aria-label="Sair"><IconClose size={26} /></button>
      </div>

      <div className="camera-stage">
        {semCamera ? (
          <p className="hint">
            Não consegui abrir a câmera aqui.<br />
            Use “Do dispositivo” ou “Da galeria” logo abaixo.
          </p>
        ) : (
          <video ref={video} autoPlay playsInline muted />
        )}
        {preparando && <div className="camera-preparando">Preparando a foto…</div>}
        {aviso && !erro && !preparando && <p className="hint">{aviso}</p>}
        {erro && !preparando && (
          <div className="camera-erro" role="alert">
            <p>{erro.recado}</p>
            {onTentarDeNovo && (
              <button className="camera-erro-btn" onClick={onTentarDeNovo}>
                Tentar de novo
              </button>
            )}
            <span className="camera-erro-codigo">{erro.codigo}</span>
          </div>
        )}
      </div>

      <div className="camera-controls">
        {/* O flash não é controlável pela web em quase nenhum aparelho. O
            ícone fica desabilitado em vez de sumir para o obturador
            continuar centralizado, como no app anterior. */}
        <span className="side apagado"><IconFlashOff size={22} /></span>
        <button className="shutter" onClick={disparar} disabled={preparando}
          aria-label="Tirar foto" />
        <button className="side" onClick={() => setFrontal((f) => !f)} aria-label="Virar câmera">
          <IconFlip size={22} />
        </button>
      </div>

      {/* Esqueceu da foto, ou comeu onde não dava pra tirar. Fica embaixo
          e menor que as outras de propósito: é a saída, não o caminho — e
          o custo está escrito no botão, antes do toque. */}
      {onSemFoto && (
        <button className="camera-semfoto" onClick={onSemFoto} disabled={preparando}>
          Sem foto? Postar só com a descrição{custoSemFoto ? ` (−${custoSemFoto})` : ""}
        </button>
      )}

      <div className="camera-foot">
        <button onClick={() => galeria.current?.click()}>
          <IconImage size={20} /> Da galeria
        </button>
        <button onClick={() => sistema.current?.click()}>
          <IconCamera size={20} /> Do dispositivo
        </button>
      </div>

      <input ref={galeria} type="file" accept="image/*" multiple hidden
        onChange={() => pegar(galeria.current)} />
      <input ref={sistema} type="file" accept="image/*" capture="environment" hidden
        onChange={() => pegar(sistema.current)} />
    </div>
  );
}

/**
 * Passo 2: conferir antes de encarar o formulário.
 *
 * Mostra TODAS as fotos já tiradas do mesmo prato, com a que está sendo
 * conferida em destaque. "Mais uma" volta pra câmera sem perder as
 * anteriores — é o que permite fotografar o prato montado e depois o
 * interior dele, que é o pedido que originou isto.
 */
export function Conferir(
  { fotos, onOk, onMais, onRemover, onCapa, onSair, onCortar }: {
    fotos: FotoPronta[];
    /** Abre o corte desta foto. */
    onCortar?: (i: number) => void;
    onOk: () => void;
    onMais: () => void;
    onRemover: (i: number) => void;
    /** Põe esta foto na frente — a capa é a única que a IA analisa. */
    onCapa: (i: number) => void;
    onSair: () => void;
  },
) {
  const [atual, setAtual] = useState(0);
  const i = Math.min(atual, Math.max(0, fotos.length - 1));
  const url = fotos[i]?.url;

  return (
    <div className="camera">
      <div className="camera-top">
        <button className="side" onClick={onSair} aria-label="Voltar"><IconClose size={26} /></button>
        <span className="camera-conta">
          {fotos.length === 1 ? "1 foto" : `${i + 1} de ${fotos.length}`}
        </span>
      </div>

      <div className="camera-stage">
        {url && <img src={url} alt="Foto do prato" />}
        {onCortar && fotos[i]?.origem && (
          <button className="camera-cortar" onClick={() => onCortar(i)}>✂️ Cortar</button>
        )}
      </div>

      {/* QUAL FOTO A IA LÊ precisa estar escrito, não deduzido.

          O servidor estima a caloria a partir da capa, que é a primeira
          da lista. Quem posta o prato junto de uma foto do lugar não tem
          como saber disso — e recebia a estimativa da foto errada sem
          nenhuma pista do porquê. */}
      {fotos.length > 1 && (
        <div className="camera-capa">
          {i === 0 ? (
            <span className="camera-capa-e">
              ★ Capa — é esta que a IA lê pra estimar a caloria
            </span>
          ) : (
            <button className="camera-capa-btn" onClick={() => { onCapa(i); setAtual(0); }}>
              ★ Usar esta como capa
            </button>
          )}
        </div>
      )}

      {fotos.length > 1 && (
        <div className="camera-tiras">
          {fotos.map((f, j) => (
            <button key={f.url} className={`camera-tira ${j === i ? "on" : ""}`}
              onClick={() => setAtual(j)} aria-label={j === 0 ? "Foto 1, a capa" : `Foto ${j + 1}`}>
              <img src={f.url} alt="" />
              {j === 0 && <span className="camera-tira-capa" aria-hidden="true">★</span>}
            </button>
          ))}
        </div>
      )}

      <div className="camera-controls conferir">
        <button className="side" onClick={() => onRemover(i)}
          aria-label={fotos.length > 1 ? "Descartar esta foto" : "Tirar outra"}>
          <IconClose size={26} />
        </button>
        <button className="camera-mais" onClick={onMais}>
          <IconPlus size={20} /> Mais uma
        </button>
        <button className="side" onClick={onOk} aria-label="Usar estas fotos">
          <IconCheck size={28} />
        </button>
      </div>
      <div className="camera-foot" />
    </div>
  );
}
